import { useId, useLayoutEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react'
import { GripVertical } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Button } from '@/ui/button'
import { Frame, FrameCard } from '@/ui/frame'
import type { PolicyOrderEntry } from '../../api/policy-order-api'

type DropTarget = { beforePolicyId: string | null }

type PolicyPositionPickerProps = {
  composition?: 'replace' | 'additive'
  policies: PolicyOrderEntry[]
  name: string
  beforePolicyId: string | null
  disabled: boolean
  onChange: (beforePolicyId: string | null) => void
}

export function PolicyPositionPicker({ composition = 'replace', policies, name, beforePolicyId, disabled, onChange }: PolicyPositionPickerProps) {
  const instructionsId = useId()
  const listRef = useRef<HTMLOListElement>(null)
  const activePointerId = useRef<number | null>(null)
  const handleRef = useRef<HTMLButtonElement>(null)
  const restoreKeyboardFocus = useRef(false)
  const [dragging, setDragging] = useState(false)
  const [dropTarget, setDropTarget] = useState<DropTarget | null>(null)
  const requestedPosition = policies.findIndex((policy) => policy.id === beforePolicyId)
  const position = requestedPosition < 0 ? policies.length : requestedPosition
  useLayoutEffect(() => {
    if (!restoreKeyboardFocus.current) return
    restoreKeyboardFocus.current = false
    handleRef.current?.focus()
  }, [position])
  if (policies.length === 0) return null

  const preview = policies.map((policy) => ({ id: policy.id, name: policy.name, draft: false }))
  preview.splice(position, 0, { id: 'new-policy', name: name.trim() || 'New policy', draft: true })

  function startDrag(event: PointerEvent<HTMLLIElement>) {
    if (disabled || event.button !== 0 || !event.isPrimary || !listRef.current) return
    event.preventDefault()
    activePointerId.current = event.pointerId
    listRef.current.setPointerCapture(event.pointerId)
    setDropTarget(null)
    setDragging(true)
  }

  function moveDrag(event: PointerEvent<HTMLOListElement>) {
    if (disabled || activePointerId.current !== event.pointerId) return
    const peers = event.currentTarget.querySelectorAll<HTMLLIElement>('[data-policy-id]')
    const nextPolicy = Array.from(peers).find((peer) => {
      const bounds = peer.getBoundingClientRect()
      return event.clientY < bounds.top + bounds.height / 2
    })
    setDropTarget({ beforePolicyId: nextPolicy?.dataset.policyId ?? null })
  }

  function clearDrag() {
    activePointerId.current = null
    setDragging(false)
    setDropTarget(null)
  }

  function endDrag(event: PointerEvent<HTMLOListElement>, commit: boolean) {
    if (activePointerId.current !== event.pointerId) return
    if (commit && !disabled && dropTarget) onChange(dropTarget.beforePolicyId)
    clearDrag()
    event.currentTarget.releasePointerCapture(event.pointerId)
  }

  function moveWithKeyboard(event: KeyboardEvent<HTMLButtonElement>) {
    if (disabled || !['ArrowUp', 'ArrowDown'].includes(event.key)) return
    event.preventDefault()
    const nextPosition = position + (event.key === 'ArrowUp' ? -1 : 1)
    if (nextPosition < 0 || nextPosition > policies.length) return
    restoreKeyboardFocus.current = true
    onChange(policies[nextPosition]?.id ?? null)
  }

  return (
    <Frame title="Policy order" description={composition === 'additive'
      ? 'All matching policies apply within this scope.'
      : 'The first matching policy wins within this scope.'}>
      <FrameCard className="flex flex-col gap-3 p-4">
        <p id={instructionsId} className="text-muted-foreground text-xs">
          Drag the new policy into position, or focus its handle and use the up and down arrow keys.
        </p>
        <ol ref={listRef} aria-label="Policy order preview" aria-live="polite"
          onPointerMove={moveDrag} onPointerUp={(event) => endDrag(event, true)}
          onPointerCancel={(event) => endDrag(event, false)} onLostPointerCapture={clearDrag}
          className="divide-border divide-y text-xs">
          {preview.map((policy) => {
            const dropBefore = !policy.draft && dropTarget?.beforePolicyId === policy.id
            const dropAfter = dropTarget !== null && dropTarget.beforePolicyId === null
              && policy.id === policies[policies.length - 1]?.id
            return <li key={policy.id} draggable={false} data-policy-id={policy.draft ? undefined : policy.id}
              onPointerDown={policy.draft ? startDrag : undefined}
              className={cn('relative flex items-center gap-3 py-2',
                policy.draft && !disabled && 'touch-none cursor-grab select-none active:cursor-grabbing',
                policy.draft && dragging && 'opacity-50')}>
              {(dropBefore || dropAfter) && (
                <span role="separator" aria-label="New policy drop position"
                  className={cn('bg-primary pointer-events-none absolute inset-x-0 h-0.5', dropBefore ? 'top-0' : 'bottom-0')} />
              )}
              {policy.draft ? (
                <Button ref={handleRef} type="button" variant="ghost" size="icon-sm" aria-label="Move new policy"
                  aria-describedby={instructionsId} disabled={disabled} onKeyDown={moveWithKeyboard}
                  className="touch-none cursor-grab active:cursor-grabbing">
                  <GripVertical aria-hidden />
                </Button>
              ) : <span aria-hidden className="size-6 shrink-0" />}
              <span title={policy.name} className={cn('min-w-0 flex-1 truncate', policy.draft ? 'font-medium' : 'text-muted-foreground')}>
                {policy.name}
              </span>
              {policy.draft && name.trim() && <span className="text-muted-foreground ml-auto shrink-0">New policy</span>}
            </li>
          })}
        </ol>
      </FrameCard>
    </Frame>
  )
}
